import { useState } from 'react';
import { useParams } from 'react-router-dom';

import styles from '../PasswordManagement.module.less';

import { Alert, Button, Col, FormControl, FormikForm, Row } from '$HVUI';
import useFetch from '$Hooks/useFetch';
import { useQuery } from '$Hooks';
import yup from '$Validation/yup';

// Minimum password strength score requirement for patient(1) or provider(3)
// Facility users may not always be "providers" (aka company_users),
// but we still want them to have strong passwords
const isPatient = !(window.isProvider || window.isFacilityUser);
const requiredStrength = isPatient ? 1 : 3;

const { boolean, date, number, object, string } = yup;

const providerValidationSchema = object().shape({
    new_password1: string().passwordStrength(requiredStrength),
    new_password2: string().confirmPassword('new_password1')
});

const patientValidationSchema = object().shape({
    isImportedUser: boolean(),
    email: string().when('isImportedUser', {
        is: true,
        then: string()
            .email('Must be a valid email address.')
            .label('Email')
            .required('Required')
    }),
    first_name: string().when('isImportedUser', {
        is: true,
        then: string().required('Required')
    }),
    last_name: string().when('isImportedUser', {
        is: true,
        then: string().required('Required')
    }),
    birthdate: date().validBirthday(),
    new_password1: string().passwordStrength(requiredStrength),
    new_password2: string().confirmPassword('new_password1'),
    password_score: number()
});

const validationSchema = isPatient
    ? patientValidationSchema
    : providerValidationSchema;

const ResetForm = props => {
    const { isBooster } = useParams();
    const [initialEmail, setInitialEmail] = useState('');
    const query = useQuery();
    const next = query.get('next');

    const {
        birthdateExempt,
        hasBirthdate,
        isImportedUser,
        initialValues,
        tokenInvalid
    } = props;
    const resetPathname = isImportedUser
        ? '/password-set-new-user/'
        : '/password-reset-confirm-with-token/';

    const { error, post, response } = useFetch(`api/v2${resetPathname}`);

    const handleSubmit = async values => {
        const {
            email,
            first_name,
            last_name,
            birthdate,
            new_password1,
            new_password2,
            uid,
            token
        } = values;
        // Provider flow only needs these fields
        let submittedValues = {
            new_password1,
            new_password2,
            uid,
            token
        };
        if (next) {
            submittedValues.next = next;
        }
        if (isImportedUser) {
            // Imported user flow needs these extra fields
            submittedValues = {
                ...submittedValues,
                email,
                first_name,
                last_name,
                birthdate
            };
            // Non-provider user flow just needs birthdate added
        } else if (!birthdateExempt) {
            submittedValues = {
                ...submittedValues,
                birthdate
            };
        }
        const responseData = await post(submittedValues);
        if (response.ok) {
            // Check if API response contains a next value
            if (responseData?.next && responseData.next.trim() !== '') {
                const nextUrl = responseData.next.trim();
                // Handle relative URLs (no protocol/host) vs absolute URLs
                const isAbsoluteUrl =
                    nextUrl.startsWith('http://') ||
                    nextUrl.startsWith('https://') ||
                    nextUrl.startsWith('//');
                // For relative URLs, ensure they start with / for root-relative paths
                const finalUrl = isAbsoluteUrl
                    ? nextUrl
                    : nextUrl.startsWith('/')
                      ? nextUrl
                      : `/${nextUrl}`;
                window.location.assign(finalUrl);
                return;
            }

            const isImportedMunged = isImportedUser && !initialEmail;
            const isEmailChanged = initialEmail && email !== initialEmail;

            if (isImportedMunged || isEmailChanged) {
                //If the user is an imported user with no email on file,
                // or if they enter an email different than the one we have
                // they need to confirm email first
                window.location.assign('/confirm-changed');
            } else {
                const verb = isImportedUser ? 'set' : 'reset';
                const message = `Your new password has been ${verb}.`;
                window.location.assign(`/?success=${message}`);
            }
        }
    };

    if (tokenInvalid && !isBooster) {
        return (
            <div className={styles['confirm-container']}>
                <Row gutter={48} align="center">
                    <Col md={12}>
                        <h1>
                            Sorry, we're not able to reset your password at this
                            time.
                        </h1>
                        <p>
                            Please submit a new request to reset your password.
                        </p>
                        <a href="https://practice.healthvana.com/forgot-password/">
                            Request to reset your password again.
                        </a>
                    </Col>
                </Row>
            </div>
        );
    }

    //patients must have a birthdate to reset their password
    if (!birthdateExempt && !hasBirthdate && !isBooster) {
        return (
            <div className={styles['confirm-container']}>
                <Row gutter={48} align="center">
                    <Col md={12}>
                        <h1>
                            Sorry, your password can't be reset automatically.
                        </h1>
                        <p>
                            <a href="https://healthvana.com/contact/">
                                Please contact us for assistance.
                            </a>
                        </p>
                    </Col>
                </Row>
            </div>
        );
    }

    let emailHelpText = '';
    if (initialValues.email) {
        emailHelpText = `Please register with a valid email address. If this is different from the one we have on file for you, we will send you an email to verify it.`;
        if (!initialEmail) {
            setInitialEmail(initialValues.email);
        }
    } else {
        emailHelpText = (
            <>
                <p>
                    <strong>
                        We need a new email address to create your account.
                    </strong>
                </p>
                <p>
                    Please register with a new email address. After completing
                    this step, we will send you an email to verify it.
                </p>
            </>
        );
    }

    let header = 'Choose a new password';
    let buttonTitle = 'Reset password';
    if (isBooster) {
        header = (
            <>
                Thank you for submitting your Covid information.
                <br />
                To save your results, finish your account set up.
            </>
        );
        buttonTitle = 'Create Account';
    }

    if (isImportedUser) {
        header = (
            <>
                Welcome to Healthvana!
                <br />
                To begin, please set your password.
            </>
        );
        buttonTitle = 'Register';
    }

    return (
        <div className={styles['confirm-container']}>
            {error && (
                <Alert
                    message="Sorry, we can't set your password right now.  Please refresh or try again later."
                    style={{ marginBottom: '1.5rem' }}
                    type="error"
                />
            )}
            <Row gutter={48} align="top">
                <Col md={12}>
                    <h1>{header}</h1>
                    <p>Here are some tips for creating a secure password:</p>
                    <div>
                        <ul>
                            <li className={styles['list-item']}>
                                Don’t use any words that contain information
                                about you, especially something someone could
                                look up.
                            </li>
                            <li className={styles['list-item']}>
                                Unusual words are best.
                            </li>
                            <li className={styles['list-item']}>
                                The longer the password, the better. Using more
                                than one word to lengthen the password is a
                                great idea.
                            </li>
                        </ul>
                    </div>
                </Col>
                <Col md={12}>
                    <div className={styles['form-container']}>
                        <FormikForm
                            initialValues={{
                                isImportedUser: isImportedUser,
                                email: window.email ? window.email : '',
                                uid: initialValues.uid,
                                token: initialValues.token,
                                password_score: 0
                            }}
                            onSubmit={handleSubmit}
                            validationSchema={validationSchema}
                        >
                            {propsFromFormik => {
                                const { isValid, dirty } = propsFromFormik;
                                return (
                                    <>
                                        {isImportedUser && (
                                            <>
                                                <FormControl
                                                    label="Email"
                                                    name="email"
                                                    errorOnChange={true}
                                                    help_text={emailHelpText}
                                                    type="email"
                                                    style={{
                                                        marginBottom: '3.5em'
                                                    }}
                                                />
                                                <FormControl
                                                    label="First Name"
                                                    name="first_name"
                                                    errorOnChange={true}
                                                    type="text"
                                                    style={{
                                                        marginBottom: '3.5em'
                                                    }}
                                                />
                                                <FormControl
                                                    label="Last Name"
                                                    name="last_name"
                                                    errorOnChange={true}
                                                    type="text"
                                                    style={{
                                                        marginBottom: '3.5em'
                                                    }}
                                                />
                                            </>
                                        )}
                                        {!birthdateExempt && (
                                            <FormControl
                                                label="Birthdate"
                                                name="birthdate"
                                                type="birthdate"
                                                errorOnChange={true}
                                                help_text="Use mm/dd/yyyy format."
                                                style={{
                                                    marginBottom: '3.5em'
                                                }}
                                            />
                                        )}
                                        <FormControl
                                            name="new_password1"
                                            type="password"
                                            label="Password"
                                            useStrengthMeter={true}
                                        />
                                        <FormControl
                                            name="new_password2"
                                            type="password"
                                            label="Confirm Password"
                                        />
                                        <FormControl name="uid" type="hidden" />
                                        <FormControl
                                            name="token"
                                            type="hidden"
                                        />
                                        <Button
                                            {...props}
                                            disabled={!(isValid && dirty)}
                                            id="button-submit"
                                            type="submit"
                                            level="primary"
                                            text={buttonTitle}
                                        />
                                    </>
                                );
                            }}
                        </FormikForm>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default ResetForm;
